// src/components/EngineerDashboard.js
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Alert,
  Avatar,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Divider,
  Tooltip,
  Collapse,
  CircularProgress,
  Snackbar,
} from "@mui/material";

import LogoutIcon from "@mui/icons-material/Logout";
import WorkIcon from "@mui/icons-material/Work";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotesIcon from "@mui/icons-material/Notes";
import SettingsIcon from "@mui/icons-material/Settings";
import HistoryIcon from "@mui/icons-material/History";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { db } from "../firebase";
import NotificationHandler from "./NotificationHandler";
import {
  collection,
  query,
} from "firebase/firestore";
import { where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers";
import { generateJobSheetPDF } from "../utils/jobSheetPDFGenerator"; // Updated path
import { formatDate, formatTime, formatDateFull } from "../utils/formatHelpers";


function EngineerDashboard({ user, onLogout, onJobSelect, onSettings }) {
  const [jobs, setJobs] = useState([]);
  const [engineerName, setEngineerName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showPastJobs, setShowPastJobs] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Load jobs for this engineer
  useEffect(() => {
    if (!user || !user.uid) return;

    setLoading(true);

    const q = query(
      collection(db, "jobs"),
      where("engineerId", "==", user.uid)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = [];
        snap.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
        setJobs(list);
        setLoading(false);
      },
      (error) => {
        console.error("EngineerDashboard error:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  // Load engineer name
  useEffect(() => {
    if (!user || !user.uid) return;

    const fetchName = async () => {
      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          const data = snap.data();
          const fullName = `${data.firstName || ""} ${data.lastName || ""}`.trim();
          if (fullName) {
            setEngineerName(fullName);
            return;
          }
        }

        setEngineerName(user.displayName || user.email || "Engineer");
      } catch (err) {
        console.error("Error loading engineer name:", err);
        setEngineerName(user.displayName || user.email || "Engineer");
      }
    };

    fetchName();
  }, [user]);

  // Separate active and past jobs
  const { activeJobs, pastJobs, stats } = useMemo(() => {
    const active = jobs.filter(j => !["Completed", "Incomplete"].includes(j.status));
    const past = jobs.filter(j => ["Completed", "Incomplete"].includes(j.status));

    // Sort active by scheduled date (ascending)
    active.sort((a, b) => {
      const aDate = a.scheduledDateTime?.seconds || 0;
      const bDate = b.scheduledDateTime?.seconds || 0;
      return aDate - bDate;
    });

    // Sort past by completion date (descending - most recent first)
    past.sort((a, b) => {
      const aDate = a.completionData?.completedAt?.seconds || a.updatedAt?.seconds || 0;
      const bDate = b.completionData?.completedAt?.seconds || b.updatedAt?.seconds || 0;
      return bDate - aDate;
    });

    // Stats
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayJobs = jobs.filter(j => {
      if (!j.scheduledDateTime) return false;
      try {
        const schedDate = j.scheduledDateTime.toDate();
        schedDate.setHours(0, 0, 0, 0);
        return schedDate.getTime() === today.getTime();
      } catch {
        return false;
      }
    }).length;

    return {
      activeJobs: active,
      pastJobs: past,
      stats: {
        total: jobs.length,
        active: active.length,
        completed: past.filter(j => j.status === "Completed").length,
        pending: jobs.filter(j => j.status === "Pending").length,
        todayJobs,
      },
    };
  }, [jobs]);

  // Filter active jobs by search
  const filteredActiveJobs = useMemo(() => {
    if (!searchQuery) return activeJobs;

    const q = searchQuery.toLowerCase();
    return activeJobs.filter(j =>
      j.customerName?.toLowerCase().includes(q) ||
      j.endCustomerName?.toLowerCase().includes(q) ||
      j.vehicleReg?.toLowerCase().includes(q) ||
      j.jobTemplateName?.toLowerCase().includes(q) ||
      j.siteAddress?.toLowerCase().includes(q) ||
      j.endCustomerAddress?.toLowerCase().includes(q)
    );
  }, [activeJobs, searchQuery]);

  // Filter past jobs by search
  const filteredPastJobs = useMemo(() => {
    if (!searchQuery) return pastJobs;

    const q = searchQuery.toLowerCase();
    return pastJobs.filter(j =>
      j.customerName?.toLowerCase().includes(q) ||
      j.endCustomerName?.toLowerCase().includes(q) ||
      j.vehicleReg?.toLowerCase().includes(q) ||
      j.jobTemplateName?.toLowerCase().includes(q) ||
      j.siteAddress?.toLowerCase().includes(q) ||
      j.endCustomerAddress?.toLowerCase().includes(q)
    );
  }, [pastJobs, searchQuery]);

  // PDF Generation (identical to JobViewer.js)
  const handleDownloadPDF = async (job, e) => {
    e.stopPropagation();
    setGeneratingPDF(job.id);
    const result = await generateJobSheetPDF(job, null, engineerName); // Pass engineerName for PDF generation
    if (result.success) {
      setSnackbar({ open: true, message: "PDF downloaded successfully!", severity: "success" });
    } else {
      setSnackbar({ open: true, message: "Could not generate PDF: " + result.error, severity: "error" });
    }
    setGeneratingPDF(null);
  };

  // Render job item
  const renderJobItem = (job, isPastJob = false) => {
    const scheduled = job.scheduledDateTime;
    const scheduledDate = formatDate(scheduled);
    const scheduledTime = formatTime(scheduled);
    const completedDate = job.completionData?.completedAt ? formatDate(job.completionData.completedAt) : null;

    return (
      <ListItemButton
        key={job.id}
        onClick={() => onJobSelect && onJobSelect(job.id)}
        sx={{
          borderRadius: 2,
          mb: 1,
          p: 2,
          transition: "all 0.2s",
          border: "1px solid rgba(148,163,184,0.1)",
          "&:hover": {
            bgcolor: "rgba(59,130,246,0.1)",
            borderColor: "primary.main",
            transform: "translateX(4px)",
          },
        }}
      >
        <ListItemText
          primary={
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {job.jobTemplateName || "Job"} #{job.jobReferenceNumber}
                </Typography>

                <Stack spacing={0.5}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    <Typography variant="body2" color="text.secondary">
                      👤 {job.endCustomerName || job.customerName || "No client"}
                    </Typography>
                    {job.endCustomerName && (
                      <Chip 
                        label={`via ${job.customerName}`} 
                        size="small" 
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <DirectionsCarIcon fontSize="small" sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary">
                      {job.vehicleReg || "No vehicle"}
                    </Typography>
                  </Box>

                  {(job.endCustomerAddress || job.siteAddress) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <LocationOnIcon fontSize="small" sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {job.endCustomerAddress || job.siteAddress}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon fontSize="small" sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary">
                      {isPastJob && completedDate
                        ? `Completed: ${completedDate}`
                        : `${scheduledDate} ${scheduledTime && `at ${scheduledTime}`}`
                      }
                    </Typography>
                  </Box>

                  {!isPastJob && job.jobNotes && (
                    <Box 
                      sx={{ 
                        display: "flex", 
                        alignItems: "flex-start", 
                        gap: 0.5,
                        mt: 0.5,
                        p: 1,
                        bgcolor: "rgba(251,191,36,0.1)",
                        borderRadius: 1,
                        border: "1px solid rgba(251,191,36,0.3)",
                      }}
                    >
                      <NotesIcon fontSize="small" sx={{ fontSize: 16, color: "#fbbf24", mt: 0.1 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: "#fbbf24",
                          fontSize: "0.8rem",
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {job.jobNotes}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                <Chip
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span>{getStatusIcon(job.status)}</span>
                      <span>{job.status || "Unknown"}</span>
                    </Box>
                  }
                  color={getStatusColor(job.status)}
                  size="small"
                  sx={{ flexShrink: 0 }}
                />
                
                {isPastJob && (
                  <Tooltip title="Download Job Sheet PDF">
                    <IconButton
                      size="small"
                      onClick={(e) => handleDownloadPDF(job, e)}
                      disabled={generatingPDF === job.id}
                      sx={{
                        bgcolor: "rgba(59,130,246,0.1)",
                        "&:hover": { bgcolor: "rgba(59,130,246,0.2)" },
                      }}
                    >
                      {generatingPDF === job.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DownloadIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Stack>
          }
        />
      </ListItemButton>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "radial-gradient(circle at top left, #1d4ed8 0%, #020617 45%, #020617 100%)",
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Silently saves FCM token if permission already granted */}
      <NotificationHandler user={user} />

      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                fontSize: "1.75rem",
                fontWeight: 700,
              }}
            >
              {engineerName?.[0]?.toUpperCase() || "E"}
            </Avatar>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Welcome back,
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {engineerName || "Engineer"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.todayJobs} {stats.todayJobs === 1 ? "job" : "jobs"} scheduled today
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Settings">
              <IconButton
                onClick={onSettings}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  border: "1px solid rgba(148,163,184,0.3)",
                  bgcolor: "rgba(15,23,42,0.5)",
                  "&:hover": {
                    bgcolor: "rgba(59,130,246,0.2)",
                    borderColor: "primary.main",
                  },
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Sign out">
              <IconButton
                onClick={onLogout}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  border: "1px solid rgba(148,163,184,0.3)",
                  bgcolor: "rgba(15,23,42,0.5)",
                  "&:hover": {
                    bgcolor: "rgba(239,68,68,0.2)",
                    borderColor: "error.main",
                  },
                }}
              >
                <LogoutIcon sx={{ mr: 1 }} />
                <Typography variant="body2">Logout</Typography>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{
              background: "linear-gradient(135deg, rgba(37,99,235,0.2), rgba(15,23,42,0.9))",
              border: "1px solid rgba(59,130,246,0.3)",
            }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
                    <WorkIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stats.total}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Jobs
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card sx={{
              background: "linear-gradient(135deg, rgba(251,191,36,0.2), rgba(15,23,42,0.9))",
              border: "1px solid rgba(251,191,36,0.3)",
            }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: "warning.main", width: 40, height: 40 }}>
                    <PendingActionsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stats.active}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card sx={{
              background: "linear-gradient(135deg, rgba(16,185,129,0.2), rgba(15,23,42,0.9))",
              border: "1px solid rgba(16,185,129,0.3)",
            }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: "success.main", width: 40, height: 40 }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stats.completed}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card sx={{
              background: "linear-gradient(135deg, rgba(139,92,246,0.2), rgba(15,23,42,0.9))",
              border: "1px solid rgba(139,92,246,0.3)",
            }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: "secondary.main", width: 40, height: 40 }}>
                    <CalendarTodayIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stats.todayJobs}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Today
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search */}
        <Paper
          elevation={6}
          sx={{
            borderRadius: 3,
            p: 2.5,
            mb: 2,
            background: "linear-gradient(135deg, rgba(15,23,42,0.95), rgba(15,23,42,0.9))",
            border: "1px solid rgba(148,163,184,0.25)",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search by client, vehicle, address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Active Jobs List */}
        <Paper
          elevation={6}
          sx={{
            borderRadius: 3,
            p: 2.5,
            mb: 2,
            background: "linear-gradient(135deg, rgba(15,23,42,0.95), rgba(15,23,42,0.9))",
            border: "1px solid rgba(148,163,184,0.25)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
            <WorkIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Active Jobs ({filteredActiveJobs.length})
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading your jobs...
              </Typography>
            </Box>
          ) : filteredActiveJobs.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Active Jobs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All caught up! New jobs will appear here when assigned to you.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredActiveJobs.map((job) => renderJobItem(job, false))}
            </List>
          )}
        </Paper>

        {/* Past Jobs Section */}
        {pastJobs.length > 0 && (
          <Paper
            elevation={6}
            sx={{
              borderRadius: 3,
              p: 2.5,
              background: "linear-gradient(135deg, rgba(15,23,42,0.95), rgba(15,23,42,0.9))",
              border: "1px solid rgba(148,163,184,0.25)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
              onClick={() => setShowPastJobs(!showPastJobs)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HistoryIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Past Jobs ({filteredPastJobs.length})
                </Typography>
              </Box>
              <IconButton size="small">
                {showPastJobs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={showPastJobs}>
              <Divider sx={{ my: 2 }} />
              {filteredPastJobs.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No past jobs match your search
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filteredPastJobs.map((job) => renderJobItem(job, true))}
                </List>
              )}
            </Collapse>
          </Paper>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EngineerDashboard;